/**
 * ACTIVITY-CORE (L3, POS variant) — "Activity" page: who did what in the
 * venue. Local-first source: bucket `activity_recent` (~90 days); older
 * history is paginated on demand from the API (IMMUTABLE master log).
 *
 * View by SHIFT (cashbox record): feed is filterable by cashbox
 * session and shows anti-fraud shift summary (voids, discounts,
 * reopenings) intended for cash balancing.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { History, Percent, RotateCcw, XCircle } from "@fleet/icons";
import {
  type ActivityRow,
  getActivityFeedQuery,
  getSessionsQuery,
  queryKeys,
  summarizeShiftActivity,
} from "@malla/shared";
import { createPowerSyncQuery } from "@malla/shared/solid";
import type { RegisterSession } from "@malla/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { ActivityList } from "./activity-list";

const LOCAL_FEED_LIMIT = 200;
const API_PAGE_SIZE = 50;
const ALL_SESSIONS = "__all__";

/** API row (camelCase) → local snake_case form of the feed. */
function mapApiActivityRow(item: {
  id: string;
  scopeId: string;
  branchId?: string | null;
  registerSessionId?: string | null;
  entityType: string;
  entityId: string;
  action: string;
  actorUserId: string;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs: string;
  clientTs?: string | null;
  patch: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}): ActivityRow {
  return {
    id: item.id,
    scope_id: item.scopeId,
    branch_id: item.branchId ?? null,
    register_session_id: item.registerSessionId ?? null,
    entity_type: item.entityType,
    entity_id: item.entityId,
    action: item.action,
    actor_user_id: item.actorUserId,
    actor_name: item.actorName ?? null,
    actor_device_id: item.actorDeviceId ?? null,
    platform: item.platform ?? null,
    server_ts: item.serverTs,
    client_ts: item.clientTs ?? null,
    patch: typeof item.patch === "string" ? item.patch : JSON.stringify(item.patch ?? {}),
    batch_id: item.batchId ?? null,
    reverts_activity_id: item.revertsActivityId ?? null,
  };
}

export function ActivityFeedPage(props: { initialSessionId?: string }) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const ctx = useActiveContext();

  // Shift filter (cash session). Initialized ONCE from URL
  // (?session=…, cash close link); afterwards pure local state —
  // without URL→state effect that could revert selection (CC-16 pattern).
  const [sessionFilter, setSessionFilter] = createSignal<string>(
    props.initialSessionId ?? ALL_SESSIONS,
  );

  // Deep history (API), accumulated below the local feed.
  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const activeSessionId = () => (sessionFilter() === ALL_SESSIONS ? undefined : sessionFilter());

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(userId(), { registerSessionId: activeSessionId() }),
    db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getActivityFeedQuery(dbInstance, {
            limit: LOCAL_FEED_LIMIT,
            registerSessionId: activeSessionId(),
          })
        : null;
    },
    enabled: () => Boolean(drizzle()),
  });

  // Cash sessions for active branch (for shift selector).
  const sessionsQuery = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.malla.sessions(ctx.branch()?.id ?? "none"),
    db,
    queryFn: () => {
      const d = drizzle();
      const org = ctx.org();
      const branch = ctx.branch();
      return d && org && branch ? getSessionsQuery(d, org.id, branch.id) : null;
    },
    enabled: () => Boolean(drizzle() && ctx.org() && ctx.branch()),
  });

  const sessionOptions = createMemo(() => {
    const opts = [{ value: ALL_SESSIONS, label: t(msg`All activity`) }];
    for (const s of sessionsQuery.data ?? []) {
      const opened = s.opened_at ? new Date(s.opened_at).toLocaleString(locale()) : s.id;
      const status = s.status === "open" ? t(msg`open`) : t(msg`closed`);
      opts.push({ value: s.id, label: `${opened} — ${status}` });
    }
    return opts;
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const sid = activeSessionId();
    const older = sid ? olderRows().filter((r) => r.register_session_id === sid) : olderRows();
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...older.filter((r) => !seen.has(r.id))];
  });

  // Anti-fraud summary of selected shift (for balancing).
  const shiftSummary = createMemo(() =>
    activeSessionId() ? summarizeShiftActivity(rows()) : null,
  );

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const last = rows()[rows().length - 1];
      const { data, error } = await api.GET("/api/activity", {
        params: {
          query: {
            before: last?.id ?? null,
            limit: API_PAGE_SIZE,
            registerSessionId: activeSessionId() ?? null,
          },
        },
      });
      if (error || !data) {
        showToast(t(msg`Could not load older activity`), "error");
        return;
      }
      const mapped = data.items.map(mapApiActivityRow);
      setOlderRows((prev) => [...prev, ...mapped]);
      setHasMoreOlder(Boolean(data.nextBefore));
    } catch {
      showToast(t(msg`Could not load older activity`), "error");
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div class="space-y-6" data-testid="activity-page">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <History class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-2xl font-bold tracking-tight">{t(msg`Activity`)}</h1>
            <p class="text-sm text-muted-foreground">
              {t(msg`Who did what in your restaurant — every void, discount and reopen.`)}
            </p>
          </div>
        </div>
        <div class="min-w-52">
          <Select
            options={sessionOptions()}
            value={sessionFilter()}
            onChange={(value) => {
              setSessionFilter(value ?? ALL_SESSIONS);
              setOlderRows([]);
              setHasMoreOlder(true);
            }}
            aria-label={t(msg`Filter by register session`)}
            data-testid="activity-session-filter"
            fluid
          />
        </div>
      </div>

      <Show when={shiftSummary()}>
        {(summary) => (
          <div
            class="grid grid-cols-3 gap-3 rounded-xl border border-border/30 bg-card/60 p-4"
            data-testid="shift-summary"
          >
            <div class="flex items-center gap-2.5">
              <XCircle class="h-4 w-4 text-destructive" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-voids">
                  {summary().voids}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Voids`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <Percent class="h-4 w-4 text-warning" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-discounts">
                  {summary().discounts}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Discounts`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <RotateCcw class="h-4 w-4 text-primary" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-reopens">
                  {summary().reopens}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Reopens`)}</p>
              </div>
            </div>
          </div>
        )}
      </Show>

      <ActivityList
        rows={rows}
        isLoading={() => localQuery.isFetching && (localQuery.data ?? []).length === 0}
        grouped
        currentUserId={userId}
        canRevert
      />

      <Show when={rows().length >= API_PAGE_SIZE && hasMoreOlder()}>
        <div class="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingOlder()}
            onClick={() => void loadOlder()}
            data-testid="activity-load-older"
          >
            {loadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
